import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from './Web3Context.jsx';
import { VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI } from './contract.js';

const VotingDashboard = () => {
    const { provider, signer, account } = useWeb3();
    const [candidates, setCandidates] = useState([]);
    const [votingStatus, setVotingStatus] = useState('상태 확인 중...');
    const [isWhitelisted, setIsWhitelisted] = useState(false);
    const [hasVoted, setHasVoted] = useState(false);
    const [isVoting, setIsVoting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchVotingData = useCallback(async () => {
        if (!provider) return;
        try {
            const contract = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, provider);
            
            // Promise.all을 사용하여 병렬로 컨트랙트 데이터를 읽어와 RPC 호출 최적화
            const [allCandidates, isActive, startTime, endTime, isPaused, isEnded, userWhitelisted, userVoted] = await Promise.all([
                contract.getAllCandidates(),
                contract.checkVotingActive(),
                contract.startTime(),
                contract.endTime(),
                contract.isVotingPaused(),
                contract.isVotingEnded(),
                account ? contract.isWhitelisted(account) : Promise.resolve(false),
                account ? contract.hasVoted(account) : Promise.resolve(false)
            ]);

            // 악의적 XSS(Javascript 주입) 방지를 위한 Protocol 검증 헬퍼 함수
            const getSafeUrl = (url) => {
                try {
                    const parsed = new URL(url);
                    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') ? url : 'https://via.placeholder.com/220x150?text=Security+Blocked';
                } catch {
                    return 'https://via.placeholder.com/220x150?text=Invalid+URL';
                }
            };

            // 후보자 데이터 포맷팅
            const formattedCandidates = allCandidates.map(c => ({
                id: c.id.toString(),
                name: c.name,
                photoUrl: getSafeUrl(c.photoUrl),
                voteCount: c.voteCount.toString()
            }));
            setCandidates(formattedCandidates);
            setIsWhitelisted(userWhitelisted);
            setHasVoted(userVoted);

            // 상태 및 타이머 판별 로직
            const now = Math.floor(Date.now() / 1000);
            const start = Number(startTime);
            const end = Number(endTime);

            if (start === 0) setVotingStatus('투표 준비 중 (관리자 시작 대기)');
            else if (isEnded) setVotingStatus('종료됨 (관리자 강제 종료)');
            else if (isPaused) setVotingStatus('일시 정지됨 (관리자 수동 정지)');
            else if (now < start) setVotingStatus('진행 전');
            else if (now > end) setVotingStatus('종료됨');
            else if (isActive) setVotingStatus('진행 중');
            else if (formattedCandidates.length < 2) setVotingStatus('진행 불가 (후보자 부족)');
            else setVotingStatus('상태 알 수 없음');

        } catch (error) {
            console.error("데이터 로딩 실패:", error);
            setVotingStatus('데이터 로딩 에러 (컨트랙트 주소를 확인하세요)');
        } finally {
            setIsLoading(false);
        }
    }, [provider, account]);

    // 무한 루프(Polling) 방지: 컴포넌트 마운트 시 1회만 호출
    useEffect(() => {
        fetchVotingData();
    }, [fetchVotingData]);

    // Phase 8: 투표 트랜잭션 실행 로직
    const handleVote = async (candidateId) => {
        if (!signer) return;
        try {
            setIsVoting(true);
            // 쓰기 작업을 위해 provider 대신 signer를 연결한 컨트랙트 인스턴스 생성
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            const tx = await contractWithSigner.vote(candidateId);
            
            await tx.wait(); // 블록에 트랜잭션이 확정될 때까지 대기 (Pending UI 유지)
            alert("투표가 성공적으로 완료되었습니다!");
            await fetchVotingData(); // 최신 득표수 및 투표 완료 상태로 화면 즉시 갱신
        } catch (error) {
            console.error("투표 에러:", error);
            if (error.code === 'ACTION_REJECTED') {
                alert("사용자가 메타마스크 트랜잭션 서명을 거부했습니다.");
            } else if (error.reason) {
                alert(`투표 실패: ${error.reason}`);
            } else {
                alert("투표 처리 중 오류가 발생했습니다.");
            }
        } finally {
            setIsVoting(false);
        }
    };

    return (
        <div style={{ marginTop: '30px', padding: '20px', borderTop: '2px solid #eee' }}>
            <h2>투표 대시보드</h2>
            <div style={{ marginBottom: '20px', fontSize: '18px' }}>
                <p>투표 상태: <strong>{votingStatus}</strong></p>
            </div>
            
            {isLoading ? (
                <p>컨트랙트에서 후보자 정보를 불러오는 중입니다...</p>
            ) : candidates.length === 0 ? (
                <p style={{ color: 'gray' }}>등록된 후보자가 없습니다.</p>
            ) : (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {candidates.map(candidate => (
                        <div key={candidate.id} style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '8px', width: '220px' }}>
                            <img 
                                src={candidate.photoUrl} 
                                alt={candidate.name} 
                                // 사이드 이펙트 방지: URL이 깨졌거나 로드 실패 시 디폴트 이미지로 대체
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/220x150?text=No+Image'; }}
                                style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                            <h3 style={{ margin: '10px 0' }}>{candidate.name}</h3>
                            <p style={{ fontSize: '20px', fontWeight: 'bold' }}>득표수: {candidate.voteCount}표</p>
                            <button 
                                disabled={votingStatus !== '진행 중' || hasVoted || !isWhitelisted || isVoting}
                                onClick={() => handleVote(candidate.id)}
                                style={{ width: '100%', padding: '10px', cursor: (votingStatus !== '진행 중' || hasVoted || !isWhitelisted || isVoting) ? 'not-allowed' : 'pointer', backgroundColor: (votingStatus !== '진행 중' || hasVoted || !isWhitelisted || isVoting) ? '#ccc' : '#4CAF50', color: (votingStatus !== '진행 중' || hasVoted || !isWhitelisted || isVoting) ? '#666' : 'white', border: 'none', borderRadius: '4px' }}
                            >
                                {isVoting ? '트랜잭션 진행 중...' 
                                    : hasVoted ? '투표 완료' 
                                    : !isWhitelisted ? '투표 권한 없음' 
                                    : votingStatus !== '진행 중' ? '투표 불가' 
                                    : '투표하기'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default VotingDashboard;